import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  Shield, Lock, Mail, Eye, EyeOff, Check, X, 
  AlertCircle, RefreshCw, Key, Smartphone
} from 'lucide-react';

export default function SecuritySettings() {
  const { token, user, logout } = useAuth();
  const { currentColors } = useTheme();
  
  const [mfaStatus, setMfaStatus] = useState({ mfa_enabled: false, mfa_method: null });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Disable MFA modal
  const [showDisableMFA, setShowDisableMFA] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  
  useEffect(() => {
    loadMFAStatus();
  }, []);

  const loadMFAStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/mfa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMfaStatus(response.data);
    } catch (error) {
      console.error('Failed to load MFA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const enableMFA = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/auth/mfa/enable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMfaStatus({ mfa_enabled: true, mfa_method: 'email' });
      alert('MFA enabled! You will receive a verification code via email on each login.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to enable MFA');
    } finally {
      setActionLoading(false);
    }
  };

  const disableMFA = async () => {
    if (!disablePassword) {
      alert('Please enter your password');
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.post(`${API}/auth/mfa/disable`, 
        { password: disablePassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMfaStatus({ mfa_enabled: false, mfa_method: null });
      setShowDisableMFA(false);
      setDisablePassword('');
      alert('MFA disabled');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to disable MFA');
    } finally {
      setActionLoading(false);
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('One lowercase letter');
    if (!/\d/.test(password)) errors.push('One number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('One special character');
    return errors;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    const validationErrors = validatePassword(newPassword);
    if (validationErrors.length > 0) {
      setPasswordError(`Password requirements: ${validationErrors.join(', ')}`);
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, 
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPasswordSuccess('Password changed successfully! Please login again.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        logout();
      }, 2000);
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setActionLoading(false);
    }
  };

  const passwordStrength = () => {
    if (!newPassword) return { strength: 0, label: '', color: '' };
    const errors = validatePassword(newPassword);
    const strength = 5 - errors.length;
    if (strength <= 1) return { strength: 20, label: 'Very Weak', color: '#ef4444' };
    if (strength === 2) return { strength: 40, label: 'Weak', color: '#f97316' };
    if (strength === 3) return { strength: 60, label: 'Fair', color: '#eab308' };
    if (strength === 4) return { strength: 80, label: 'Good', color: '#22c55e' };
    return { strength: 100, label: 'Strong', color: '#10b981' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: currentColors.primary }} />
      </div>
    );
  }

  const strength = passwordStrength();

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Shield className="w-8 h-8" style={{ color: currentColors.primary }} />
        <h1 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>
          Security Settings
        </h1>
      </div>

      {/* MFA Section */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <Smartphone className="w-6 h-6" style={{ color: currentColors.accent }} />
          <h2 className="text-lg font-cinzel" style={{ color: currentColors.text }}>
            Two-Factor Authentication (MFA)
          </h2>
        </div>
        
        <p className="text-sm mb-4" style={{ color: currentColors.textMuted }}>
          Add an extra layer of security to your account. When enabled, you'll receive a verification 
          code via email each time you log in.
        </p>
        
        <div 
          className="p-4 rounded-xl flex items-center justify-between"
          style={{ 
            backgroundColor: mfaStatus.mfa_enabled ? 'rgba(34, 197, 94, 0.1)' : `${currentColors.textMuted}10`,
            border: `1px solid ${mfaStatus.mfa_enabled ? 'rgba(34, 197, 94, 0.3)' : currentColors.border}`
          }}
        >
          <div className="flex items-center gap-3">
            {mfaStatus.mfa_enabled ? (
              <Check className="w-5 h-5 text-green-500" />
            ) : (
              <X className="w-5 h-5" style={{ color: currentColors.textMuted }} />
            )}
            <div>
              <p className="font-medium" style={{ color: currentColors.text }}>
                {mfaStatus.mfa_enabled ? 'MFA Enabled' : 'MFA Disabled'}
              </p>
              {mfaStatus.mfa_enabled && (
                <p className="text-xs" style={{ color: currentColors.textMuted }}>
                  Method: Email verification code
                </p>
              )}
            </div>
          </div>
          
          {mfaStatus.mfa_enabled ? (
            <button
              onClick={() => setShowDisableMFA(true)}
              disabled={actionLoading}
              className="px-4 py-2 rounded-xl text-sm text-red-400 hover:bg-red-500/10 transition-all"
            >
              Disable
            </button>
          ) : (
            <button
              onClick={enableMFA}
              disabled={actionLoading}
              className="cosmic-btn text-sm"
            >
              {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Enable MFA'}
            </button>
          )}
        </div>
      </div>

      {/* Change Password Section */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Key className="w-6 h-6" style={{ color: currentColors.secondary }} />
            <h2 className="text-lg font-cinzel" style={{ color: currentColors.text }}>
              Change Password
            </h2>
          </div>
          {!showChangePassword && (
            <button
              onClick={() => setShowChangePassword(true)}
              className="text-sm px-4 py-2 rounded-xl transition-all"
              style={{ 
                backgroundColor: `${currentColors.secondary}20`,
                color: currentColors.secondary
              }}
            >
              Change
            </button>
          )}
        </div>
        
        {!showChangePassword ? (
          <p className="text-sm" style={{ color: currentColors.textMuted }}>
            It's a good practice to change your password regularly.
          </p>
        ) : (
          <form onSubmit={handleChangePassword} className="space-y-4">
            {/* Current Password */}
            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="cosmic-input w-full pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: currentColors.textMuted }}
                >
                  {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            {/* New Password */}
            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="cosmic-input w-full pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: currentColors.textMuted }}
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Password strength indicator */}
              {newPassword && (
                <div className="mt-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: currentColors.textMuted }}>Password strength:</span>
                    <span style={{ color: strength.color }}>{strength.label}</span>
                  </div>
                  <div 
                    className="h-1.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: `${currentColors.textMuted}20` }}
                  >
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ 
                        width: `${strength.strength}%`,
                        backgroundColor: strength.color
                      }}
                    />
                  </div>
                </div>
              )}
              
              {/* Password requirements */}
              <div className="mt-2 text-xs space-y-1" style={{ color: currentColors.textMuted }}>
                <p className={newPassword.length >= 8 ? 'text-green-500' : ''}>
                  {newPassword.length >= 8 ? '✓' : '○'} At least 8 characters
                </p>
                <p className={/[A-Z]/.test(newPassword) ? 'text-green-500' : ''}>
                  {/[A-Z]/.test(newPassword) ? '✓' : '○'} One uppercase letter
                </p>
                <p className={/[a-z]/.test(newPassword) ? 'text-green-500' : ''}>
                  {/[a-z]/.test(newPassword) ? '✓' : '○'} One lowercase letter
                </p>
                <p className={/\d/.test(newPassword) ? 'text-green-500' : ''}>
                  {/\d/.test(newPassword) ? '✓' : '○'} One number
                </p>
                <p className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'text-green-500' : ''}>
                  {/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? '✓' : '○'} One special character
                </p>
              </div>
            </div>
            
            {/* Confirm Password */}
            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="cosmic-input w-full"
                required
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
              )}
            </div>
            
            {/* Error/Success Messages */}
            {passwordError && (
              <div className="p-3 rounded-lg bg-red-500/10 text-red-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {passwordError}
              </div>
            )}
            {passwordSuccess && (
              <div className="p-3 rounded-lg bg-green-500/10 text-green-500 text-sm flex items-center gap-2">
                <Check className="w-4 h-4" />
                {passwordSuccess}
              </div>
            )}
            
            {/* Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowChangePassword(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordError('');
                }}
                className="flex-1 px-4 py-2 rounded-xl"
                style={{ 
                  backgroundColor: `${currentColors.textMuted}20`,
                  color: currentColors.textMuted
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={actionLoading || strength.strength < 100 || newPassword !== confirmPassword}
                className="flex-1 cosmic-btn"
              >
                {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin mx-auto" /> : 'Update Password'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Security Tips */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <Lock className="w-6 h-6" style={{ color: currentColors.primary }} />
          <h2 className="text-lg font-cinzel" style={{ color: currentColors.text }}>
            Security Tips
          </h2>
        </div>
        <ul className="space-y-2 text-sm" style={{ color: currentColors.textMuted }}>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 text-green-500" />
            Enable two-factor authentication for maximum security
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 text-green-500" />
            Use a unique password that you don't use on other sites
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 text-green-500" />
            Never share your password or verification codes with anyone
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 text-green-500" />
            Change your password periodically (every 3-6 months)
          </li>
        </ul>
      </div>

      {/* Disable MFA Modal */}
      {showDisableMFA && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div 
            className="w-full max-w-md rounded-2xl p-6"
            style={{ backgroundColor: currentColors.background }}
          >
            <h3 className="text-xl font-cinzel mb-4" style={{ color: currentColors.text }}>
              Disable Two-Factor Authentication
            </h3>
            <p className="text-sm mb-4" style={{ color: currentColors.textMuted }}>
              Enter your password to confirm disabling MFA. This will make your account less secure.
            </p>
            <input
              type="password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              placeholder="Enter your password"
              className="cosmic-input w-full mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDisableMFA(false);
                  setDisablePassword('');
                }}
                className="flex-1 px-4 py-2 rounded-xl"
                style={{ 
                  backgroundColor: `${currentColors.textMuted}20`,
                  color: currentColors.textMuted
                }}
              >
                Cancel
              </button>
              <button
                onClick={disableMFA}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30"
              >
                {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin mx-auto" /> : 'Disable MFA'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
